import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, useParams } from "react-router";
import { api, till } from "@/lib/api";
import { ago, date, fromMinor, personName, toMinor } from "@/lib/format";
import { useTill } from "@/lib/till-context";
import { METHOD_LABEL, type Method } from "@/lib/types";
import { Badge, Button, Field, Input, Modal, Select, cx, useConfirm, useToast } from "@/components/ui";
import { Card, PageHead, SETTINGS_LINKS, td, th } from "@/components/Layout";

type S = {
  discountLimit: { cashier: number; supervisor: number; manager: number };
  refundLimit: { cashier: number | null; supervisor: number | null; manager: number | null };
  overShortLimit: number;
  returnDays: number;
  refundExpiryMins?: number;
  warrantyMonths: { serial: number; qty: number };
  methods: Method[];
  requireRef: Method[];
  momoMerchant: string;
  allowNegativeStock: boolean;
  receiptHeader: string;
  receiptFooter: string;
  serialTypes: string[];
  paystackEnabled: boolean;
  manualFallback: boolean;
  displayMode: "sale" | "pick" | "pay";
  selfPayScope: "all" | "accessories";
  displayShowStock: boolean;
  displayHiddenCategories: string[];
  autoPrint: boolean;
  groupTier: Record<"retail" | "wholesale" | "distributor" | "staff", "retail" | "wholesale" | "distributor">;
  walkInWholesale: boolean;
  scanning: boolean;
  requireCustomer: boolean;
  customerRefusal: boolean;
  walkInPin: boolean;
  pinOnly: boolean;
  wholesaleNoDiscount: boolean;
  offlineSales: boolean;
  offlineMaxHours: number;
  offlineSerial: boolean;
  websiteImport: boolean;
  serials: boolean;
  delivery: { enabled: boolean; zones: { id: string; name: string; fee: number }[]; vehicles: { name: string; capacityKg: number | null }[] };
};
const OFFLINE_HOURS = [4, 12, 24, 0];
const DISPLAY_MODES: { id: S["displayMode"]; title: string; sub: string }[] = [
  { id: "sale", title: "Show the sale only", sub: "The customer screen shows what the cashier rings up, and how to pay." },
  { id: "pick", title: "Browse and pick, cashier confirms", sub: "Customers build an order and send it to the till. The cashier adds it and takes payment." },
  { id: "pay", title: "Browse, pick and pay themselves", sub: "Customers pay with Mobile Money or card on the screen, then collect at the counter with their number." },
];
type TillRow = { id: string; name: string; number: number; revoked: boolean; pairedBy: string | null; device: string | null; pairedAt: number; lastSeenAt: number | null; shift: string | null; shopId: string; setupPending: boolean };
type Shop = { id: string; name: string };
const ALL: Method[] = ["cash", "momo", "card", "credit"];
const TYPES = ["phone", "tablet", "laptop", "tv", "console", "audio", "appliance", "accessory", "power", "smart-home"];

/** Tablets have an icon menu with no sub links, so the pages are linked here too. */
function SettingsTabs() {
  return (
    <div className="no-scrollbar mb-4 flex overflow-x-auto border-b border-slate-200 xl:hidden">
      {SETTINGS_LINKS.map((l) => (
        <NavLink key={l.to} to={l.to} className={({ isActive }) => cx("flex h-11 flex-none items-center whitespace-nowrap border-b-2 px-3.5 text-sm", isActive ? "border-brand font-bold" : "border-transparent text-slate-500")}>{l.label}</NavLink>
      ))}
    </div>
  );
}

const SECTION_SUB: Record<string, string> = {
  selling: "What the shop sells, how stock is tracked, and the prices each customer group pays.",
  limits: "How much staff can do without a higher PIN, and how long customers can return items.",
  payments: "How customers pay, and what the till does when the internet drops.",
  delivery: "Delivery areas, fees and vehicles.",
  receipt: "What the receipt says and what the customer screen shows.",
};

export function Settings() {
  const { section = "selling" } = useParams();
  const show = (k: string) => (k === section ? "xl:col-span-2" : "hidden");
  const toast = useToast();
  const qc = useQueryClient();
  const { refetchMe, me } = useTill();
  const cats = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: { category: string; active: boolean }[] }>(till("/admin/items")), staleTime: 60_000 });
  const categories = [...new Set((cats.data?.items ?? []).filter((i) => i.active && i.category).map((i) => i.category))].sort();
  const q = useQuery({ queryKey: ["till-settings"], queryFn: () => api<{ settings: S }>(till("/admin/settings")) });
  const [s, setS] = useState<S | null>(null);
  const [money, setMoney] = useState<Record<string, string>>({});
  // Delivery zones and trucks as typed (fees in cedis, capacity in kg) until saved.
  const [zones, setZones] = useState<{ id: string; name: string; fee: string }[]>([]);
  const [trucks, setTrucks] = useState<{ name: string; kg: string }[]>([]);
  useEffect(() => {
    if (q.data && !s) {
      setS(q.data.settings);
      const st = q.data.settings;
      setZones((st.delivery?.zones ?? []).map((z) => ({ id: z.id, name: z.name, fee: fromMinor(z.fee) })));
      setTrucks((st.delivery?.vehicles ?? []).map((v) => ({ name: v.name, kg: v.capacityKg != null ? String(v.capacityKg) : "" })));
      setMoney({ rc: st.refundLimit.cashier == null ? "" : fromMinor(st.refundLimit.cashier), rs: st.refundLimit.supervisor == null ? "" : fromMinor(st.refundLimit.supervisor), os: fromMinor(st.overShortLimit) });
    }
  }, [q.data, s]);
  if (!s) return <p className="text-sm text-slate-500">Loading…</p>;
  const save = async () => {
    try {
      const body = {
        ...s,
        refundLimit: { cashier: money.rc === "" ? null : toMinor(money.rc!), supervisor: money.rs === "" ? null : toMinor(money.rs!) },
        overShortLimit: toMinor(money.os ?? "0"),
        delivery: {
          enabled: !!s.delivery?.enabled,
          zones: zones.filter((z) => z.name.trim()).map((z) => ({ id: z.id || z.name, name: z.name.trim(), fee: toMinor(z.fee || "0") })),
          vehicles: trucks.filter((t) => t.name.trim()).map((t) => ({ name: t.name.trim(), capacityKg: t.kg ? Number(t.kg) : null })),
        },
      };
      const r = await api<{ settings: S }>(till("/admin/settings"), { method: "PUT", body: { settings: body } });
      setS(r.settings);
      toast("Settings saved");
      refetchMe();
      void qc.invalidateQueries({ queryKey: ["till-settings"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <>
      <SettingsTabs />
      <PageHead title={SETTINGS_LINKS.find((l) => l.to.endsWith(`/${section}`))?.label ?? "Settings"} sub={SECTION_SUB[section]}>
        <Button tone="primary" onClick={save}>
          Save settings
        </Button>
      </PageHead>
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 xl:grid-cols-2">
          <Card className={show("selling")} title="What this shop sells">
            <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
              <p className="text-slate-600">This decides what the till and back office show. Pick the closest, then fine-tune.</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {(
                  [
                    ["Electronics and phones", "IMEI and warranty, a website", { serials: true, websiteImport: true }],
                    ["Building materials and hardware", "Counted stock in bags, lengths, packs. Delivery and contractor accounts", { serials: false, websiteImport: false }],
                    ["Other shop", "Counted stock only", { serials: false, websiteImport: false }],
                  ] as const
                ).map(([title, sub, v]) => {
                  // Buildwise turns Delivery on and off only from its own page.
                  const brand = !!import.meta.env.VITE_BRAND;
                  const on = s.serials === v.serials && s.websiteImport === v.websiteImport && (brand || title !== "Building materials and hardware" || !!s.delivery?.enabled) && (brand || title !== "Other shop" || !s.delivery?.enabled);
                  return (
                    <button
                      key={title}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setS({ ...s, ...v, delivery: { ...(s.delivery ?? { zones: [], vehicles: [] }), enabled: brand ? !!s.delivery?.enabled : title === "Building materials and hardware" ? true : title === "Other shop" ? false : !!s.delivery?.enabled } })}
                      className={cx("flex flex-col items-start gap-0.5 border-[1.5px] px-3 py-2.5 text-left", on ? "border-brand bg-brand-tint" : "border-slate-200 bg-white")}
                    >
                      <b>{title}</b>
                      <span className="text-xs text-slate-500">{sub}</span>
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" disabled={!!import.meta.env.VITE_BRAND} checked={s.serials} onChange={(e) => setS({ ...s, serials: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Some products are sold by their own IMEI / serial number, with a warranty
              </label>
              <span className="-mt-1.5 pl-7 text-xs text-slate-500">{s.serials ? "Phones, laptops and TVs: the till asks for each one's IMEI, receipts register the warranty, returns find the sale by scanning it." : "Everything is counted (bags, lengths, pieces). No IMEI prompts, warranty claims or serial reports."}</span>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={s.websiteImport} onChange={(e) => setS({ ...s, websiteImport: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> This shop has a website, and products come from it
              </label>
              <span className="-mt-1.5 pl-7 text-xs text-slate-500">{s.websiteImport ? "Products › Import copies website products into the shop. Stock moves between shop and website with Transfer and Bring in." : "The shop adds its own products (one by one or from Excel). No website import or transfers."}</span>
            </div>
          </Card>
          <Card className={show("delivery")} title="Delivery and the yard">
            <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!s.delivery?.enabled} onChange={(e) => setS({ ...s, delivery: { ...(s.delivery ?? { zones: [], vehicles: [] }), enabled: e.target.checked } })} className="h-5 w-5 accent-[var(--brand)]" /> We deliver to site, and goods leave from a yard
              </label>
              {s.delivery?.enabled ? (
                <>
                  <span className="text-xs text-slate-500">The till gets a Delivery button on the sale and a Yard screen for loading. Each zone's fee is added to the sale as its own line.</span>
                  <b className="text-[13px]">Zones and fees</b>
                  {zones.map((z, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <Input aria-label="Zone name" value={z.name} onChange={(e) => setZones(zones.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="e.g. Zone B · East Legon, Adenta" className="!w-64" />
                      <Input aria-label="Fee" inputMode="decimal" value={z.fee} onChange={(e) => setZones(zones.map((x, j) => (j === i ? { ...x, fee: e.target.value } : x)))} placeholder="150.00" className="!w-28 font-mono" />
                      <Button size="sm" onClick={() => setZones(zones.filter((_, j) => j !== i))}>Remove</Button>
                    </div>
                  ))}
                  {zones.length < 20 ? <Button size="sm" className="self-start" onClick={() => setZones([...zones, { id: "", name: "", fee: "" }])}>+ Add a zone</Button> : null}
                  <b className="mt-2 text-[13px]">Trucks</b>
                  {trucks.map((t, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <Input aria-label="Truck" value={t.name} onChange={(e) => setTrucks(trucks.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="e.g. Tipper · GT 4421-22" className="!w-64" />
                      carries <Input aria-label="Capacity in kg" inputMode="numeric" value={t.kg} onChange={(e) => setTrucks(trucks.map((x, j) => (j === i ? { ...x, kg: e.target.value.replace(/\D/g, "") } : x)))} placeholder="3000" className="!w-24" /> kg
                      <Button size="sm" onClick={() => setTrucks(trucks.filter((_, j) => j !== i))}>Remove</Button>
                    </div>
                  ))}
                  {trucks.length < 12 ? <Button size="sm" className="self-start" onClick={() => setTrucks([...trucks, { name: "", kg: "" }])}>+ Add a truck</Button> : null}
                </>
              ) : null}
            </div>
          </Card>
          <Card className={show("selling")} title={s.serials ? "Scanning & serials" : "Scanning & stock"}>
            <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
              <p className="text-slate-600">Any USB or Bluetooth barcode scanner works in keyboard mode, no setup. Tablets and phones scan with the camera.</p>
              {s.serials ? (
              <>
              <span className="text-[13px] font-semibold">Imported website products sold per unit (IMEI / serial)</span>
              <div className="flex flex-wrap gap-2">
                {TYPES.map((t) => (
                  <button key={t} type="button" onClick={() => setS({ ...s, serialTypes: toggle(s.serialTypes, t) })} className={cx("h-9 border px-3 text-[13px]", s.serialTypes.includes(t) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white")}>
                    {t}
                  </button>
                ))}
              </div>
              <span className="text-xs text-slate-500">A per-unit product can't be charged until each device's IMEI / serial is scanned.</span>
              </>
              ) : null}
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={s.allowNegativeStock} onChange={(e) => setS({ ...s, allowNegativeStock: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Let counted items sell below 0 (stock not yet received)
              </label>
            </div>
          </Card>
          <Card className={show("limits")} title={s.serials ? "Warranty & returns" : "Returns"}>
            <div className="grid gap-3 px-5 pb-5 sm:grid-cols-3">
              <Field label="Return window (days)">
                <Input inputMode="numeric" value={String(s.returnDays)} onChange={(e) => setS({ ...s, returnDays: Number(e.target.value.replace(/\D/g, "")) || 0 })} />
              </Field>
              {s.serials ? (
              <>
              <Field label="Warranty for devices (months)">
                <Input inputMode="numeric" value={String(s.warrantyMonths.serial)} onChange={(e) => setS({ ...s, warrantyMonths: { ...s.warrantyMonths, serial: Number(e.target.value.replace(/\D/g, "")) || 0 } })} />
              </Field>
              <Field label="Warranty for accessories (months)">
                <Input inputMode="numeric" value={String(s.warrantyMonths.qty)} onChange={(e) => setS({ ...s, warrantyMonths: { ...s.warrantyMonths, qty: Number(e.target.value.replace(/\D/g, "")) || 0 } })} />
              </Field>
              <p className="text-xs text-slate-500 sm:col-span-3">A product's own warranty (Products › edit) wins over these defaults. After the window the till offers a warranty claim instead of a refund.</p>
              </>
              ) : <p className="self-end text-xs text-slate-500 sm:col-span-2">After the window, a return needs a manager's PIN.</p>}
            </div>
          </Card>
          <Card className={show("limits")} title="Limits before a higher PIN is needed">
            <div className="grid gap-3 px-5 pb-5 sm:grid-cols-2">
              <Field label="Cashier discount (%)">
                <Input inputMode="decimal" value={String(s.discountLimit.cashier)} onChange={(e) => setS({ ...s, discountLimit: { ...s.discountLimit, cashier: Number(e.target.value) || 0 } })} />
              </Field>
              <Field label="Supervisor discount (%)">
                <Input inputMode="decimal" value={String(s.discountLimit.supervisor)} onChange={(e) => setS({ ...s, discountLimit: { ...s.discountLimit, supervisor: Number(e.target.value) || 0 } })} />
              </Field>
              <Field label="Cashier refund limit" hint="0 = every refund a cashier starts waits for approval. Blank = any">
                <Input inputMode="decimal" value={money.rc ?? ""} onChange={(e) => setMoney({ ...money, rc: e.target.value })} />
              </Field>
              <Field label="Supervisor refund limit" hint="Above this, a till manager or the back office approves. Blank = any">
                <Input inputMode="decimal" value={money.rs ?? ""} onChange={(e) => setMoney({ ...money, rs: e.target.value })} />
              </Field>
              {import.meta.env.VITE_BRAND ? (
                <Field label="A refund waiting for approval lasts" hint="Then the till cancels it and the cashier can start again">
                  <Select value={String(s.refundExpiryMins ?? 60)} onChange={(e) => setS({ ...s, refundExpiryMins: Number(e.target.value) })}>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="0">Until the end of the shift</option>
                  </Select>
                </Field>
              ) : null}
              <Field label="Cash difference allowed at close" hint="More than this needs a supervisor">
                <Input inputMode="decimal" value={money.os ?? ""} onChange={(e) => setMoney({ ...money, os: e.target.value })} />
              </Field>
            </div>
          </Card>
          <Card className={show("payments")} title="Payments · Paystack" right={<span className={cx("px-2 py-1 font-mono text-[11px]", me?.paystack?.ready ? "bg-ink text-brand-light" : "bg-warn-tint text-warn-ink")}>{me?.paystack?.ready ? "● CONNECTED" : s.paystackEnabled ? "NOT SET UP" : "OFF"}</span>}>
            <div className="flex flex-col gap-3 px-5 pb-5 text-sm">
              <p className="text-slate-600">Mobile Money and card go through the store's Paystack account (the same one the website uses). The customer pays on the customer screen, on their phone by QR, from a texted link, or approves a MoMo prompt, and each payment confirms by itself. Nobody types references.</p>
              {s.paystackEnabled && !me?.paystack?.ready ? <p className="bg-warn-tint px-3 py-2 text-[13px] text-warn-ink">Paystack keys aren't set in the store admin (Settings › Payments). Until they are, MoMo and card need a reference typed in.</p> : null}
              <label className="flex items-center gap-2.5">
                <input type="checkbox" checked={s.paystackEnabled} onChange={(e) => setS({ ...s, paystackEnabled: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Take Mobile Money and card through Paystack
              </label>
              <label className="flex items-center gap-2.5">
                <input type="checkbox" checked={s.manualFallback} onChange={(e) => setS({ ...s, manualFallback: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> If Paystack can't be used, a supervisor may record MoMo or card by hand
              </label>
              <span className="text-[13px] font-semibold">Methods at the till</span>
              <div className="flex flex-wrap gap-2">
                {ALL.map((m) => (
                  <button key={m} type="button" onClick={() => setS({ ...s, methods: toggle(s.methods, m) })} className={cx("h-10 border px-3 text-[13px]", s.methods.includes(m) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white")}>
                    {METHOD_LABEL[m]}
                  </button>
                ))}
              </div>
              <span className="text-xs text-slate-500">Recorded by hand, a payment needs its MoMo reference or card approval code, and shows on the shift report as "by hand".</span>
            </div>
          </Card>
          <Card className={show("receipt")} title="Customer display">
            <div className="flex flex-col gap-4 px-5 pb-5 text-sm">
              <div role="radiogroup" aria-label="What customers can do on their screen" className="grid gap-2 md:grid-cols-3">
                {DISPLAY_MODES.map((m) => (
                  <button key={m.id} type="button" role="radio" aria-checked={s.displayMode === m.id} onClick={() => setS({ ...s, displayMode: m.id })} className={cx("flex flex-col gap-1.5 border-2 p-4 text-left", s.displayMode === m.id ? "border-brand bg-brand-tint" : "border-slate-200 bg-white hover:border-slate-400")}>
                    <b className="text-[15px]">{m.title}</b>
                    <span className="text-[13px] text-slate-600">{m.sub}</span>
                  </button>
                ))}
              </div>
              {s.displayMode === "pay" ? (
                <>
                  {!me?.paystack?.ready ? <p className="bg-warn-tint px-3 py-2 text-[13px] text-warn-ink">Paying on the customer screen needs Paystack connected. Until then customers can only send their order to the cashier.</p> : null}
                  <div className="flex flex-wrap gap-5">
                    <label className="flex items-center gap-2"><input type="radio" name="scope" checked={s.selfPayScope === "all"} onChange={() => setS({ ...s, selfPayScope: "all" })} className="h-5 w-5 accent-[var(--brand)]" /> Customers can pay for everything (devices get their IMEI scanned at handover)</label>
                    <label className="flex items-center gap-2"><input type="radio" name="scope" checked={s.selfPayScope === "accessories"} onChange={() => setS({ ...s, selfPayScope: "accessories" })} className="h-5 w-5 accent-[var(--brand)]" /> Accessories only (orders with a device are paid at the counter)</label>
                  </div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={s.autoPrint} onChange={(e) => setS({ ...s, autoPrint: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Print the receipt by itself when a self-checkout is paid
                  </label>
                  <span className="text-xs text-slate-500">Printing with no dialog needs the till's Chrome started in kiosk printing mode (one-time setup). Otherwise the print dialog opens and the cashier taps Print.</span>
                </>
              ) : null}
              {s.displayMode !== "sale" ? (
                <>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={s.displayShowStock} onChange={(e) => setS({ ...s, displayShowStock: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Show stock to customers ("Only 2 left"). Off lists only what's in stock, with no counts.
                  </label>
                  <span className="text-[13px] font-semibold">Categories customers can browse (tap to hide one)</span>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cName) => {
                      const hidden = s.displayHiddenCategories.includes(cName);
                      return (
                        <button key={cName} type="button" onClick={() => setS({ ...s, displayHiddenCategories: toggle(s.displayHiddenCategories, cName) })} className={cx("h-10 border px-3 text-[13px]", hidden ? "border-slate-200 bg-white text-slate-400 line-through" : "border-slate-900 bg-slate-900 text-white")}>
                          {cName}
                        </button>
                      );
                    })}
                    {!categories.length ? <span className="text-xs text-slate-500">No product categories yet.</span> : null}
                  </div>
                </>
              ) : null}
            </div>
          </Card>
          <Card className={show("selling")} title="Wholesale & customer groups">
            <div className="flex flex-col gap-4 px-5 pb-5 text-sm">
              <span className="text-[13px] text-slate-600">Each product can have a wholesale and a distributor price (Products). Here: which prices each group of shop customers gets. Customers are put in a group on the Customers page.</span>
              <div className="grid gap-3 sm:grid-cols-4">
                {(["retail", "wholesale", "distributor", "staff"] as const).map((g) => (
                  <Field key={g} label={`${g[0]!.toUpperCase()}${g.slice(1)} customers pay`}>
                    <Select value={s.groupTier[g]} onChange={(e) => setS({ ...s, groupTier: { ...s.groupTier, [g]: e.target.value as S["groupTier"][typeof g] } })}>
                      <option value="retail">Retail prices</option>
                      <option value="wholesale">Wholesale prices</option>
                      <option value="distributor">Distributor prices</option>
                    </Select>
                  </Field>
                ))}
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" disabled={!!import.meta.env.VITE_BRAND} checked={s.walkInWholesale} onChange={(e) => setS({ ...s, walkInWholesale: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Wholesale for new buyers: wholesale prices for someone not on the customer list, with a supervisor's PIN</label>
              <label className="flex items-center gap-2"><input type="checkbox" disabled={!!import.meta.env.VITE_BRAND} checked={s.walkInPin} onChange={(e) => setS({ ...s, walkInPin: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Ask for a supervisor's PIN before wholesale prices, when no wholesale customer is attached</label>
              <label className="flex items-center gap-2"><input type="checkbox" disabled={!!import.meta.env.VITE_BRAND} checked={s.pinOnly || !!import.meta.env.VITE_BRAND} onChange={(e) => setS({ ...s, pinOnly: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Tellers sign in with only their PIN, with no list of names{import.meta.env.VITE_BRAND ? " (always on)" : ""}</label>
              <label className="flex items-center gap-2"><input type="checkbox" disabled={!!import.meta.env.VITE_BRAND} checked={s.scanning} onChange={(e) => setS({ ...s, scanning: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> This shop scans barcodes. Off: tellers search or tap, and barcode fields and labels are hidden</label>
              <label className="flex items-center gap-2"><input type="checkbox" disabled={!!import.meta.env.VITE_BRAND} checked={s.requireCustomer} onChange={(e) => setS({ ...s, requireCustomer: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Every sale needs a customer or company name and a phone number before it can be charged or held</label>
              <label className={cx("flex items-center gap-2", !s.requireCustomer && "opacity-50")}><input type="checkbox" disabled={!s.requireCustomer} checked={s.customerRefusal} onChange={(e) => setS({ ...s, customerRefusal: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Allow a sale without details when the customer refuses, with a manager's PIN</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={s.wholesaleNoDiscount} onChange={(e) => setS({ ...s, wholesaleNoDiscount: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> No extra discount on wholesale and distributor sales</label>
            </div>
          </Card>
          <TransferAccounts className={show("payments")} />
          <Card className={show("payments")} title="When the internet drops">
            <div className="px-5 pb-5 text-sm">
              <div className="flex max-w-2xl flex-col gap-3">
                <span className="text-[13px] text-slate-600">Sales wait on the till and send by themselves when the line is back. Staff who have signed in on a till before can sign in on it offline. Returns, payment links and closing the shift need the line.</span>
                <label className="flex items-center gap-2"><input type="checkbox" checked={s.offlineSales} onChange={(e) => setS({ ...s, offlineSales: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Keep selling when the internet drops</label>
                {s.serials ? (
                  <>
                <label className={cx("flex items-center gap-2", !s.offlineSales && "opacity-50")}><input type="checkbox" disabled={!s.offlineSales} checked={s.offlineSerial} onChange={(e) => setS({ ...s, offlineSerial: e.target.checked })} className="h-5 w-5 accent-[var(--brand)]" /> Sell IMEI / serial items offline too</label>
                <span className="text-xs text-slate-500">Off is safer: with two tills offline, both could sell the same phone. The second sale would be refused when it sends, after the customer has paid.</span>
                  </>
                ) : null}
                <span className="text-[13px] font-semibold">Stop selling offline after</span>
                <div className={cx("flex flex-wrap gap-2", !s.offlineSales && "pointer-events-none opacity-50")}>
                  {OFFLINE_HOURS.map((h) => (
                    <button key={h} type="button" onClick={() => setS({ ...s, offlineMaxHours: h })} className={cx("h-10 border px-3 text-[13px]", s.offlineMaxHours === h ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white")}>
                      {h ? `${h} hours` : "No limit"}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-slate-500">Counted from the oldest sale still waiting to send. Past it, the till stops selling until it's back online. Each till shows whether it's ready to work offline, and its own look, under Till settings on its sign-in screen.</span>
              </div>
            </div>
          </Card>
          <Card className={show("receipt")} title="Receipt">
            <div className="grid gap-3 px-5 pb-5 sm:grid-cols-2">
              <Field label="Header (address, phone)">
                <textarea rows={3} value={s.receiptHeader} onChange={(e) => setS({ ...s, receiptHeader: e.target.value })} className="resize-none border border-slate-200 px-3 py-2 text-sm outline-none" />
              </Field>
              <Field label="Footer (returns / warranty line)">
                <textarea rows={3} value={s.receiptFooter} onChange={(e) => setS({ ...s, receiptFooter: e.target.value })} className="resize-none border border-slate-200 px-3 py-2 text-sm outline-none" />
              </Field>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

/** The bank accounts and MoMo numbers cash can be transferred to. */
function TransferAccounts({ className }: { className?: string }) {
  const toast = useToast();
  const q = useQuery({ queryKey: ["cash-accounts"], queryFn: () => api<{ accounts: { name: string; kind: string }[] }>(till("/admin/cash/accounts")) });
  const [draft, setDraft] = useState<{ name: string; kind: string }[] | null>(null);
  const list = draft ?? q.data?.accounts ?? [];
  const save = async () => {
    try {
      await api(till("/admin/cash/accounts"), { method: "PUT", body: { accounts: list } });
      toast("Accounts saved");
      setDraft(null);
      void q.refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save", "err");
    }
  };
  return (
    <Card className={className} title="Cash transfer accounts" right={draft ? <Button size="sm" tone="primary" onClick={() => void save()}>Save accounts</Button> : undefined}>
      <div className="flex flex-col gap-2 px-5 pb-5">
        <p className="text-[13px] text-slate-500">Where cash can be sent from the safe. They appear as a list on Cash control.</p>
        {list.map((a, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Select aria-label="Type" value={a.kind} onChange={(e) => setDraft(list.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))} className="!h-10 !w-44"><option value="bank">Bank</option><option value="momo">Mobile Money</option></Select>
            <Input aria-label="Account name" value={a.name} onChange={(e) => setDraft(list.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder={a.kind === "momo" ? "e.g. MTN MoMo 024 000 0000" : "e.g. GCB Main account"} maxLength={60} className="!h-10 min-w-0 flex-1" />
            <Button size="sm" onClick={() => setDraft(list.filter((_, j) => j !== i))}>Remove</Button>
          </div>
        ))}
        <Button size="sm" className="self-start" onClick={() => setDraft([...list, { name: "", kind: "bank" }])}>Add an account</Button>
      </div>
    </Card>
  );
}

/** Shops and their tills: a page of its own, set up in three steps. */
export function ShopsAndTills() {
  return (
    <>
      <SettingsTabs />
      <PageHead title="Shops & tills" sub="Add your shops and tills here. Each device then selects its shop and till during setup." />
      <ol className="mb-4 grid gap-3 sm:grid-cols-3">
        {[["Add a shop", "Type the shop's name. It gets its own setup code."], ["Add its tills", "Name each till, like Front counter."], ["Set up the device", "Type the shop's code on the device, then choose its till."]].map(([t, d], i) => (
          <li key={t} className="flex items-center gap-3 border border-slate-200 bg-white p-3.5">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-brand-tint text-sm font-extrabold text-brand-ink">{i + 1}</span>
            <span className="min-w-0"><b className="block text-sm">{t}</b><span className="text-xs text-slate-500">{d}</span></span>
          </li>
        ))}
      </ol>
      <Tills />
    </>
  );
}

function Tills() {
  const toast = useToast();
  const confirm = useConfirm();
  const q = useQuery({ queryKey: ["tills"], queryFn: () => api<{ tills: TillRow[] }>(till("/admin/tills")) });
  const [code, setCode] = useState<{ code: string; expiresAt: number } | null>(null);
  const make = async () => {
    try {
      setCode(await api<{ code: string; expiresAt: number }>(till("/admin/pair-code"), { body: {} }));
    } catch (e) {
      toast(e instanceof Error ? e.message : "Failed", "err");
    }
  };
  const revoke = async (t: TillRow) => {
    if (!(await confirm({ title: `Remove ${t.name} from this device?`, body: "It signs out at once and will need setting up again.", ok: "Remove", danger: true }))) return;
    try {
      await api(till(`/admin/tills/${t.id}`), { method: "PUT", body: { revoked: true } });
      void q.refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Failed", "err");
    }
  };
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: Shop[] }>(till("/admin/shops")) });
  const shops = shopsQ.data?.shops ?? [];
  // Buildwise: every shop has its own setup code.
  const codesQ = useQuery({ queryKey: ["shop-codes", shops.length], queryFn: () => api<{ codes: Record<string, string> }>(till("/admin/shop-codes")), enabled: !!import.meta.env.VITE_BRAND && shops.length > 0 });
  const showCode = (c: string) => `${c.slice(0, 4)}-${c.slice(4)}`;
  const removeShop = async (s: Shop) => {
    if (!(await confirm({ title: `Remove ${s.name}?`, body: "Its tills sign out, its setup code stops working and its products stop selling. Past sales stay in your reports. This can't be undone here.", ok: "Remove shop", danger: true }))) return;
    try {
      await api(till(`/admin/shops/${s.id}/remove`), { body: { name: s.name } });
      toast(`${s.name} removed`);
      void q.refetch();
      void shopsQ.refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't remove the shop", "err");
    }
  };
  const newCode = async (s: Shop) => {
    if (!(await confirm({ title: `Make a new code for ${s.name}?`, body: "The old code stops working straight away. Tills already set up keep working.", ok: "Make a new code" }))) return;
    try {
      await api(till(`/admin/shops/${s.id}/code`), { body: {} });
      void codesQ.refetch();
      toast("New code made");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Failed", "err");
    }
  };
  // Names are always typed by the admin, never made up.
  const [ask, setAsk] = useState<{ title: string; label: string; example: string; value: string; save: (name: string) => Promise<unknown> } | null>(null);
  const saveAsk = async () => {
    if (!ask) return;
    try {
      await ask.save(ask.value);
      setAsk(null);
      void q.refetch();
      void shopsQ.refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Failed", "err");
    }
  };
  const addShop = () => setAsk({ title: "Add a shop", label: "Shop name", example: "e.g. Spintex branch", value: "", save: (name) => api(till("/admin/shops"), { body: { name } }) });
  const renameShop = (s: Shop) => setAsk({ title: "Rename this shop", label: "Shop name", example: "", value: s.name, save: (name) => api(till(`/admin/shops/${s.id}`), { method: "PUT", body: { name } }) });
  const addTill = (s: Shop) => setAsk({ title: `Add a till to ${s.name}`, label: "Till name", example: "e.g. Front counter", value: "", save: (name) => api(till("/admin/tills"), { body: { name, shopId: s.id } }) });
  const rename = (t: TillRow) => setAsk({ title: "Rename this till", label: "Till name", example: "", value: t.name, save: (name) => api(till(`/admin/tills/${t.id}`), { method: "PUT", body: { name } }) });
  const askModal = ask ? (
    <Modal title={ask.title} onClose={() => setAsk(null)} footer={<><Button onClick={() => setAsk(null)}>Cancel</Button><Button tone="primary" onClick={() => void saveAsk()}>Save</Button></>}>
      <Field label={ask.label}><Input autoFocus maxLength={40} value={ask.value} placeholder={ask.example} onChange={(e) => setAsk({ ...ask, value: e.target.value })} /></Field>
    </Modal>
  ) : null;
  if (shops.length) {
    const rows = q.data?.tills ?? [];
    const loose = rows.filter((t) => !t.revoked && !shops.some((x) => x.id === t.shopId));
    return (
      <>
      <Card title="Your shops" right={<span className="flex gap-2">{import.meta.env.VITE_BRAND ? null : <Button size="sm" onClick={make}>Get a setup code</Button>}<Button size="sm" tone="primary" onClick={addShop}>Add a shop</Button></span>}>
        {code ? <div className="mx-5 mb-3 flex flex-wrap items-center gap-4 bg-brand-tint px-4 py-3 text-brand-ink"><span className="font-mono text-2xl font-bold tracking-[0.2em]">{code.code}</span><span className="text-sm">Open the till on the new device and type this code, then select its shop and till. Works once, for 15 minutes.</span></div> : null}
        <div className="grid gap-3 px-5 pb-5 lg:grid-cols-3">
          {shops.map((s) => {
            const mine = rows.filter((t) => t.shopId === s.id && !t.revoked);
            return (
              <section key={s.id} className="flex flex-col border border-slate-200 bg-white">
                <div className="flex items-start justify-between gap-2 px-4 pt-3.5">
                  <span className="min-w-0"><b className="block truncate text-base">{s.name}</b><span className="text-xs text-slate-500">{mine.length} till{mine.length === 1 ? "" : "s"} · {mine.filter((t) => t.shift).length} open now</span></span>
                  <span className="flex flex-none gap-3">
                    <button type="button" className="h-10 text-[13px] font-semibold text-brand" onClick={() => renameShop(s)}>Rename</button>
                    {import.meta.env.VITE_BRAND && (shops.length > 1 || !mine.length) ? <button type="button" className="h-10 text-[13px] font-semibold text-red-700" onClick={() => void removeShop(s)}>Remove</button> : null}
                  </span>
                </div>
                {codesQ.data?.codes[s.id] ? (
                  <div className="mx-4 mt-2.5 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-brand-tint px-3 py-2 text-brand-ink">
                    <span className="min-w-0"><span className="block text-xs font-semibold">Setup code</span><b className="font-mono text-[17px] tracking-[0.12em]">{showCode(codesQ.data.codes[s.id]!)}</b></span>
                    <span className="flex gap-3 text-[13px] font-bold">
                      <button type="button" className="min-h-10" onClick={() => void navigator.clipboard?.writeText(showCode(codesQ.data!.codes[s.id]!)).then(() => toast("Code copied"))}>Copy</button>
                      <button type="button" className="min-h-10" onClick={() => void newCode(s)}>New code</button>
                    </span>
                  </div>
                ) : null}
                <div className="flex flex-col py-1.5">
                  {mine.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 border-t border-slate-100 px-4 py-2 text-sm">
                      <span className="min-w-0 flex-1"><b className="block truncate">{t.name}</b><span className="block truncate text-xs text-slate-500">{t.setupPending ? "Waiting for setup" : t.shift ? `${personName(t.shift)} on shift` : `Paired · last seen ${ago(t.lastSeenAt)}`}</span></span>
                      <button type="button" className="h-10 text-[13px] font-semibold text-brand" onClick={() => rename(t)}>Rename</button>
                      {!t.setupPending ? <button type="button" className="h-10 text-[13px] font-semibold text-red-700" onClick={() => void revoke(t)}>Remove</button> : null}
                    </div>
                  ))}
                </div>
                {mine.length < 1 ? <p className="bg-warn-tint px-4 py-2 text-[13px] font-semibold text-warn-ink">This shop needs at least 1 till.</p> : null}
                <div className="mt-auto p-3"><Button size="sm" className="w-full" onClick={() => addTill(s)}>+ Add a till</Button></div>
              </section>
            );
          })}
        </div>
        {askModal}
      </Card>
      {loose.length ? (
        <Card title="Tills not in a shop" className="mt-4">
          <p className="px-5 pb-2 text-[13px] text-slate-500">Set up before you added shops. They keep working until you remove them.</p>
          {loose.map((t) => (
            <div key={t.id} className="flex items-center gap-2 border-t border-slate-100 px-5 py-2 text-sm">
              <span className="min-w-0 flex-1"><b className="block truncate">{t.name}</b><span className="block truncate text-xs text-slate-500">Last seen {ago(t.lastSeenAt)}</span></span>
              <button type="button" className="h-10 text-[13px] font-semibold text-brand" onClick={() => rename(t)}>Rename</button>
              <button type="button" className="h-10 text-[13px] font-semibold text-red-700" onClick={() => void revoke(t)}>Remove</button>
            </div>
          ))}
        </Card>
      ) : null}
      </>
    );
  }
  const selectOnly = !!import.meta.env.VITE_BRAND;
  if (selectOnly && !(q.data?.tills ?? []).some((t) => !t.revoked)) {
    return (
      <Card>
        {askModal}
        <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
          <b className="text-base">No shops yet</b>
          <p className="text-sm text-slate-500">Add your first shop, then add its tills.</p>
          <Button tone="primary" onClick={addShop}>Add a shop</Button>
        </div>
      </Card>
    );
  }
  return (
    <Card title={selectOnly ? "Tills not in a shop" : "Tills"} right={<span className="flex gap-2">{selectOnly ? <Button size="sm" tone="primary" onClick={addShop}>Add a shop</Button> : <><Button size="sm" onClick={addShop}>Set up shops</Button><Button size="sm" onClick={make}>Add a till (setup code)</Button></>}</span>}>
      {askModal}
      {code ? (
        <div className="mx-5 mb-3 flex flex-wrap items-center gap-4 bg-brand-tint px-4 py-3 text-brand-ink">
          <span className="font-mono text-2xl font-bold tracking-[0.2em]">{code.code}</span>
          <span className="text-sm">Open the till ({window.location.host.replace(/^office\./, "pos.")}) on the new device, choose "Setup code" and type it. Works once, for 15 minutes.</span>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {["Till", "Set up", "Last seen", "Shift", ""].map((h) => (
                <th key={h} className={th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(q.data?.tills ?? []).map((t) => (
              <tr key={t.id} className={cx(t.revoked && "opacity-50")}>
                <td className={td}>
                  <b>{t.name}</b> {t.revoked ? <Badge>Removed</Badge> : null}
                  <span className="block max-w-[340px] truncate text-xs text-slate-500">{t.device}</span>
                </td>
                <td className={cx(td, "text-slate-500")}>
                  {date(t.pairedAt)} · {t.pairedBy}
                </td>
                <td className={cx(td, "text-slate-500")}>{ago(t.lastSeenAt)}</td>
                <td className={td}>{t.shift ?? "-"}</td>
                <td className={td}>
                  {!t.revoked ? (
                    <span className="flex gap-3 text-[13px] font-semibold">
                      <button type="button" className="text-brand" onClick={() => void rename(t)}>
                        Rename
                      </button>
                      <button type="button" className="text-red-700" onClick={() => void revoke(t)}>
                        Remove
                      </button>
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
